import { z } from 'zod';
import { paginationMetaSchema } from '@/api/pagination';

export const zonalStatementRowSchema = z.object({
  userId: z.coerce.number(),
  name: z.string(),
  hrEmpId: z.string().nullable(),
  mobile: z.string().nullable(),
  contactNo: z.string().nullable(),
  departmentName: z.string().nullable(),
  designationName: z.string().nullable(),
  divisionName: z.string().nullable(),
  districtName: z.string().nullable(),
  thanaName: z.string().nullable(),
  dcrAmount: z.number(),
  derAmount: z.number(),
  specimenQty: z.number(),
});

export const zonalStatementResponseSchema = z.object({
  data: z.array(zonalStatementRowSchema),
  meta: paginationMetaSchema.extend({
    totals: z.object({
      dcrAmount: z.number(),
      derAmount: z.number(),
      specimenQty: z.number(),
    }),
  }),
});

export type ZonalStatementRow = z.infer<typeof zonalStatementRowSchema>;
export type ZonalStatementResponse = z.infer<typeof zonalStatementResponseSchema>;

const statementMemberSchema = z.object({
  userId: z.coerce.number(),
  name: z.string(),
  hrEmpId: z.string().nullable(),
  mobile: z.string().nullable(),
  departmentName: z.string().nullable(),
  designationName: z.string().nullable(),
  divisionName: z.string().nullable(),
  districtName: z.string().nullable(),
  thanaName: z.string().nullable(),
});

export const expenseStatementSchema = z.object({
  member: statementMemberSchema,
  period: z.object({ dateFrom: z.string(), dateTo: z.string() }),
  days: z.array(
    z.object({
      date: z.coerce.date(),
      ta: z.number(),
      teacherEntertainment: z.number(),
      pb: z.number(),
      da: z.number(),
      other: z.number(),
      total: z.number(),
    }),
  ),
  byExpenseType: z.array(
    z.object({
      expenseTypeId: z.coerce.number(),
      code: z.string().nullable(),
      name: z.string(),
      claimed: z.number(),
      approved: z.number(),
    }),
  ),
  totals: z.object({
    ta: z.number(),
    teacherEntertainment: z.number(),
    pb: z.number(),
    da: z.number(),
    other: z.number(),
    total: z.number(),
  }),
});

export const specimenStatementSchema = z.object({
  member: statementMemberSchema,
  period: z.object({ dateFrom: z.string(), dateTo: z.string() }),
  items: z.array(
    z.object({
      specimenId: z.coerce.number(),
      code: z.string().nullable(),
      name: z.string(),
      nameLocal: z.string().nullable(),
      netPrice: z.number(),
      bookTypeName: z.string().nullable(),
      qty: z.number(),
      challanReceived: z.number(),
      value: z.number(),
    }),
  ),
  totals: z.object({
    qty: z.number(),
    challanReceived: z.number(),
    value: z.number(),
  }),
});

export type ExpenseStatement = z.infer<typeof expenseStatementSchema>;
export type ExpenseStatementDay = ExpenseStatement['days'][number];
export type ExpenseTypeTotal = ExpenseStatement['byExpenseType'][number];
export type ExpenseStatementTotals = ExpenseStatement['totals'];
export type SpecimenStatement = z.infer<typeof specimenStatementSchema>;
export type SpecimenStatementItem = SpecimenStatement['items'][number];
export type SpecimenStatementTotals = SpecimenStatement['totals'];

export const zonalStatementSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  userId: z.coerce.number().optional(),
  divisionId: z.coerce.number().optional(),
  districtId: z.coerce.number().optional(),
});

export type ZonalStatementSearch = z.infer<typeof zonalStatementSearchSchema>;
